import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  GridLegacy as Grid,
  TextField,
  Button,
  Chip,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  Paper,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Settings as SettingsIcon,
  EmojiEvents as TrophyIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { useNavigate } from 'react-router-dom';

interface LoyaltyLevel {
  id: string;
  name: string;
  icon: string;
  color: string;
  pointsRequired: number;
  monthlyPointsRequired: number;
  benefits: string[];
  enabled: boolean;
  order: number;
}

interface Feature {
  id: string;
  name: string;
  description: string;
  levels: string[]; // which levels have access to this feature
  enabled: boolean;
}

interface LoyaltyConfig {
  levels: LoyaltyLevel[];
  features: Feature[];
  pointsPerCommission: number; // points earned per $1 commission
  monthlyResetEnabled: boolean;
  levelDowngradeEnabled: boolean;
  maintenanceThreshold: number; // percentage required to maintain level
  bronzeRequirements: {
    profileCompleted: boolean;
    xmAccountVerified: boolean;
    tradingExperienceRequired: boolean;
    preferredPairsRequired: boolean;
  };
  updatedAt?: any;
  updatedBy?: string;
}

const DEFAULT_CONFIG: LoyaltyConfig = {
  levels: [
    {
      id: 'bronze',
      name: 'Bronze',
      icon: '🥉',
      color: '#CD7F32',
      pointsRequired: 0,
      monthlyPointsRequired: 0,
      benefits: [
        'Access to basic trading signals',
        'Weekly market analysis',
        'Community chat access',
        'Educational content library'
      ],
      enabled: true,
      order: 1,
    },
    {
      id: 'silver',
      name: 'Silver',
      icon: '🥈',
      color: '#C0C0C0',
      pointsRequired: 500,
      monthlyPointsRequired: 500,
      benefits: [
        'All Bronze benefits',
        'Premium trading signals',
        'Daily market insights',
        'Priority support',
        'Advanced charting tools'
      ],
      enabled: true,
      order: 2,
    },
    {
      id: 'gold',
      name: 'Gold',
      icon: '🥇',
      color: '#FFD700',
      pointsRequired: 1000,
      monthlyPointsRequired: 1000,
      benefits: [
        'All Silver benefits',
        'VIP trading signals',
        'Personal account manager',
        'Exclusive webinars',
        'Custom EA development'
      ],
      enabled: true,
      order: 3,
    },
  ],
  features: [
    {
      id: 'trading_signals',
      name: 'Trading Signals',
      description: 'Access to trading signals and market alerts',
      levels: ['bronze', 'silver', 'gold'],
      enabled: true,
    },
    {
      id: 'market_analysis',
      name: 'Market Analysis',
      description: 'Daily and weekly market analysis reports',
      levels: ['bronze', 'silver', 'gold'],
      enabled: true,
    },
    {
      id: 'premium_signals',
      name: 'Premium Signals',
      description: 'High-accuracy premium trading signals',
      levels: ['silver', 'gold'],
      enabled: true,
    },
    {
      id: 'personal_manager',
      name: 'Personal Account Manager',
      description: 'Dedicated account manager for personalized support',
      levels: ['gold'],
      enabled: true,
    },
    {
      id: 'exclusive_webinars',
      name: 'Exclusive Webinars',
      description: 'Access to VIP-only trading webinars',
      levels: ['gold'],
      enabled: true,
    },
  ],
  pointsPerCommission: 10,
  monthlyResetEnabled: true,
  levelDowngradeEnabled: true,
  maintenanceThreshold: 75,
  bronzeRequirements: {
    profileCompleted: true,
    xmAccountVerified: true,
    tradingExperienceRequired: true,
    preferredPairsRequired: true,
  },
};

// Predefined application features linked to app pages
const APP_FEATURES: Feature[] = [
  { id: 'ai_analyses', name: 'AI Analyses', description: 'AI-powered technical & fundamental analyses', levels: ['bronze','silver','gold'], enabled: true },
  { id: 'chart', name: 'Chart', description: 'Interactive trading charts', levels: ['bronze','silver','gold'], enabled: true },
  { id: 'market_intelligence', name: 'Market Intelligence', description: 'News & sentiment dashboard', levels: ['bronze','silver','gold'], enabled: true },
  { id: 'expert_advisors', name: 'Expert Advisors', description: 'Custom EA downloads and tools', levels: ['silver','gold'], enabled: true },
  { id: 'signals', name: 'Signals', description: 'Trading signal service', levels: ['bronze','silver','gold'], enabled: true },
  { id: 'trading_journal', name: 'Trading Journal', description: 'In-app trading journal with analytics', levels: ['bronze','silver','gold'], enabled: true },
  { id: 'education', name: 'Education', description: 'Educational resources & courses', levels: ['bronze','silver','gold'], enabled: true },
  { id: 'economic_calendar', name: 'Economic Calendar', description: 'Economic events calendar', levels: ['bronze','silver','gold'], enabled: true },
  { id: 'competitions', name: 'Competitions', description: 'Trading competitions & leaderboards', levels: ['bronze','silver','gold'], enabled: true },
  { id: 'feedback', name: 'Feedback and Suggestions', description: 'Submit feedback to improve the platform', levels: ['bronze','silver','gold'], enabled: true },
  { id: 'community', name: 'Community', description: 'Community chat & groups', levels: ['bronze','silver','gold'], enabled: true },
];

const mergeFeatures = (existing: Feature[]): Feature[] => {
  const map: Record<string, Feature> = {};
  existing.forEach(f => { map[f.id] = f; });
  return APP_FEATURES.map(f => map[f.id] ? { ...f, ...map[f.id] } : f);
};

export const AdminLoyaltyConfig: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [config, setConfig] = useState<LoyaltyConfig>(DEFAULT_CONFIG);
  const [editLevel, setEditLevel] = useState<LoyaltyLevel | null>(null);
  const [editFeature, setEditFeature] = useState<Feature | null>(null);
  const [levelDialog, setLevelDialog] = useState(false);
  const [featureDialog, setFeatureDialog] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Check admin status and load config
  useEffect(() => {
    const loadAdminData = async () => {
      if (!currentUser) {
        navigate('/login');
        return;
      }

      try {
        // Check admin status
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (!userDoc.exists() || !userDoc.data().isAdmin) {
          setError('Access denied. Admin privileges required.');
          setTimeout(() => navigate('/dashboard'), 2000);
          return;
        }

        setIsAdmin(true);

        // Load loyalty config
        const configDoc = await getDoc(doc(db, 'config', 'loyalty'));
        if (configDoc.exists()) {
          const data = configDoc.data() as LoyaltyConfig;
          setConfig({ ...data, features: mergeFeatures(data.features || []) });
        } else {
          // Create default config
          await setDoc(doc(db, 'config', 'loyalty'), {
            ...DEFAULT_CONFIG,
            updatedAt: serverTimestamp(),
            updatedBy: currentUser.uid,
          });
          setConfig(DEFAULT_CONFIG);
        }
      } catch (error) {
        console.error('Error loading config:', error);
        setError('Failed to load configuration.');
      } finally {
        setLoading(false);
      }
    };

    loadAdminData();
  }, [currentUser, navigate]);

  const handleSaveConfig = async () => {
    try {
      setSaving(true);
      await updateDoc(doc(db, 'config', 'loyalty'), {
        ...config,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser?.uid,
      });
      setSuccess('Configuration saved successfully.');
    } catch (error) {
      console.error('Error saving config:', error);
      setError('Failed to save configuration.');
    } finally {
      setSaving(false);
    }
  };

  const handleEditLevel = (level: LoyaltyLevel) => {
    setEditLevel({ ...level });
    setLevelDialog(true);
  };

  const handleSaveLevel = () => {
    if (!editLevel) return;

    const updatedLevels = config.levels.map(level =>
      level.id === editLevel.id ? editLevel : level
    );

    setConfig({ ...config, levels: updatedLevels });
    setLevelDialog(false);
    setEditLevel(null);
  };

  const handleEditFeature = (feature: Feature) => {
    setEditFeature({ ...feature });
    setFeatureDialog(true);
  };

  const handleSaveFeature = () => {
    if (!editFeature) return;

    const updatedFeatures = config.features.map(feature =>
      feature.id === editFeature.id ? editFeature : feature
    );

    setConfig({ ...config, features: updatedFeatures });
    setFeatureDialog(false);
    setEditFeature(null);
  };

  const handleAddBenefit = (levelId: string) => {
    const prompt = window.prompt('Enter new benefit:');
    if (prompt && prompt.trim()) {
      const updatedLevels = config.levels.map(level =>
        level.id === levelId
          ? { ...level, benefits: [...level.benefits, prompt.trim()] }
          : level
      );
      setConfig({ ...config, levels: updatedLevels });
    }
  };

  const handleRemoveBenefit = (levelId: string, benefitIndex: number) => {
    const updatedLevels = config.levels.map(level =>
      level.id === levelId
        ? { ...level, benefits: level.benefits.filter((_, index) => index !== benefitIndex) }
        : level
    );
    setConfig({ ...config, levels: updatedLevels });
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', pt: { xs: 12, md: 14 }, pb: 4 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        </Container>
      </Box>
    );
  }

  if (!isAdmin) {
    return (
      <Box sx={{ minHeight: '100vh', pt: { xs: 12, md: 14 }, pb: 4 }}>
        <Container maxWidth="lg">
          <Alert severity="error" sx={{ mt: 4 }}>
            {error || 'Access denied. Admin privileges required.'}
          </Alert>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', pt: { xs: 12, md: 14 }, pb: 4 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h3" component="h1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <TrophyIcon sx={{ fontSize: 40 }} />
              XM Loyalty Configuration
            </Typography>
            <Typography variant="h6" color="text.secondary">
              Configure loyalty levels, features, and requirements
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSaveConfig}
            disabled={saving}
            size="large"
          >
            {saving ? <CircularProgress size={20} /> : 'Save Configuration'}
          </Button>
        </Box>

        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ mb: 4 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 4 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* General Settings */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SettingsIcon />
                  General Settings
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      label="Points per $1 Commission"
                      type="number"
                      value={config.pointsPerCommission}
                      onChange={(e) => setConfig({
                        ...config,
                        pointsPerCommission: parseInt(e.target.value) || 0
                      })}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      label="Maintenance Threshold (%)"
                      type="number"
                      value={config.maintenanceThreshold}
                      onChange={(e) => setConfig({
                        ...config,
                        maintenanceThreshold: parseInt(e.target.value) || 0
                      })}
                      helperText="Percentage of points required to maintain level"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={config.monthlyResetEnabled}
                          onChange={(e) => setConfig({
                            ...config,
                            monthlyResetEnabled: e.target.checked
                          })}
                        />
                      }
                      label="Monthly Reset Enabled"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={config.levelDowngradeEnabled}
                          onChange={(e) => setConfig({
                            ...config,
                            levelDowngradeEnabled: e.target.checked
                          })}
                        />
                      }
                      label="Level Downgrade Enabled"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Bronze Requirements */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Bronze Level Requirements
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={config.bronzeRequirements.profileCompleted}
                          onChange={(e) => setConfig({
                            ...config,
                            bronzeRequirements: {
                              ...config.bronzeRequirements,
                              profileCompleted: e.target.checked
                            }
                          })}
                        />
                      }
                      label="Profile Completed"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={config.bronzeRequirements.xmAccountVerified}
                          onChange={(e) => setConfig({
                            ...config,
                            bronzeRequirements: {
                              ...config.bronzeRequirements,
                              xmAccountVerified: e.target.checked
                            }
                          })}
                        />
                      }
                      label="XM Account Verified"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={config.bronzeRequirements.tradingExperienceRequired}
                          onChange={(e) => setConfig({
                            ...config,
                            bronzeRequirements: {
                              ...config.bronzeRequirements,
                              tradingExperienceRequired: e.target.checked
                            }
                          })}
                        />
                      }
                      label="Trading Experience Required"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={config.bronzeRequirements.preferredPairsRequired}
                          onChange={(e) => setConfig({
                            ...config,
                            bronzeRequirements: {
                              ...config.bronzeRequirements,
                              preferredPairsRequired: e.target.checked
                            }
                          })}
                        />
                      }
                      label="Preferred Pairs Required"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Loyalty Levels */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Loyalty Levels Configuration
                </Typography>
                {config.levels.map((level) => (
                  <Accordion key={level.id}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                        <Typography variant="h4">{level.icon}</Typography>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="h6">{level.name} Level</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {level.monthlyPointsRequired} points/month • {level.benefits.length} benefits
                          </Typography>
                        </Box>
                        <Chip
                          label={level.enabled ? 'Enabled' : 'Disabled'}
                          color={level.enabled ? 'success' : 'default'}
                          size="small"
                        />
                        <IconButton
                          component="span"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditLevel(level);
                          }}
                          sx={{ cursor: 'pointer' }}
                        >
                          <EditIcon />
                        </IconButton>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle2" gutterBottom>
                            Requirements
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Monthly Points: {level.monthlyPointsRequired}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Total Points: {level.pointsRequired}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="subtitle2">
                              Benefits ({level.benefits.length})
                            </Typography>
                            <Button
                              size="small"
                              startIcon={<AddIcon />}
                              onClick={() => handleAddBenefit(level.id)}
                            >
                              Add Benefit
                            </Button>
                          </Box>
                          <List dense>
                            {level.benefits.map((benefit, index) => (
                              <ListItem key={index} divider>
                                <ListItemText primary={benefit} />
                                <ListItemSecondaryAction>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleRemoveBenefit(level.id, index)}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </ListItemSecondaryAction>
                              </ListItem>
                            ))}
                          </List>
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </CardContent>
            </Card>
          </Grid>

          {/* Features Configuration */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Features Configuration
                </Typography>
                {config.features.map((feature) => (
                  <Paper key={feature.id} sx={{ p: 2, mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1">{feature.name}</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {feature.description}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          {feature.levels.map((levelId) => (
                            <Chip
                              key={levelId}
                              size="small"
                              label={config.levels.find(l => l.id === levelId)?.icon + ' ' + levelId}
                              sx={{
                                bgcolor: config.levels.find(l => l.id === levelId)?.color + '20',
                                color: config.levels.find(l => l.id === levelId)?.color,
                              }}
                            />
                          ))}
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Switch
                          checked={feature.enabled}
                          onChange={(e) => {
                            const updatedFeatures = config.features.map(f =>
                              f.id === feature.id ? { ...f, enabled: e.target.checked } : f
                            );
                            setConfig({ ...config, features: updatedFeatures });
                          }}
                        />
                        <IconButton onClick={() => handleEditFeature(feature)}>
                          <EditIcon />
                        </IconButton>
                      </Box>
                    </Box>
                  </Paper>
                ))}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Edit Level Dialog */}
        <Dialog open={levelDialog} onClose={() => setLevelDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Edit {editLevel?.name} Level</DialogTitle>
          <DialogContent>
            {editLevel && (
              <Box sx={{ pt: 2 }}>
                <TextField
                  fullWidth
                  label="Level Name"
                  value={editLevel.name}
                  onChange={(e) => setEditLevel({ ...editLevel, name: e.target.value })}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Icon/Emoji"
                  value={editLevel.icon}
                  onChange={(e) => setEditLevel({ ...editLevel, icon: e.target.value })}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Color (Hex)"
                  value={editLevel.color}
                  onChange={(e) => setEditLevel({ ...editLevel, color: e.target.value })}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Monthly Points Required"
                  type="number"
                  value={editLevel.monthlyPointsRequired}
                  onChange={(e) => setEditLevel({ ...editLevel, monthlyPointsRequired: parseInt(e.target.value) || 0 })}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Total Points Required"
                  type="number"
                  value={editLevel.pointsRequired}
                  onChange={(e) => setEditLevel({ ...editLevel, pointsRequired: parseInt(e.target.value) || 0 })}
                  sx={{ mb: 2 }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={editLevel.enabled}
                      onChange={(e) => setEditLevel({ ...editLevel, enabled: e.target.checked })}
                    />
                  }
                  label="Enabled"
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setLevelDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveLevel} variant="contained">
              Save Changes
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Feature Dialog */}
        <Dialog open={featureDialog} onClose={() => setFeatureDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Edit Feature</DialogTitle>
          <DialogContent>
            {editFeature && (
              <Box sx={{ pt: 2 }}>
                <TextField
                  fullWidth
                  label="Feature Name"
                  value={editFeature.name}
                  onChange={(e) => setEditFeature({ ...editFeature, name: e.target.value })}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  rows={3}
                  value={editFeature.description}
                  onChange={(e) => setEditFeature({ ...editFeature, description: e.target.value })}
                  sx={{ mb: 2 }}
                />
                <Typography variant="subtitle2" gutterBottom>
                  Available for Levels:
                </Typography>
                {config.levels.map((level) => (
                  <FormControlLabel
                    key={level.id}
                    control={
                      <Checkbox
                        checked={editFeature.levels.includes(level.id)}
                        onChange={(e) => {
                          const newLevels = e.target.checked
                            ? [...editFeature.levels, level.id]
                            : editFeature.levels.filter(l => l !== level.id);
                          setEditFeature({ ...editFeature, levels: newLevels });
                        }}
                      />
                    }
                    label={`${level.icon} ${level.name}`}
                  />
                ))}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setFeatureDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveFeature} variant="contained">
              Save Changes
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
}; 